import type { LucideIcon } from "lucide-react";
import { Compass, Heart, Sparkles, Target } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AnimatedSection } from "@/src/common/components/animated-section";
import { EyebrowBadge } from "@/src/common/components/eyebrow-badge";
import { PageHelmet } from "@/src/common/components/page-helmet";
import { Reveal } from "@/src/common/components/reveal";
import { SectionHeading } from "@/src/common/components/section-heading";

interface BeliefEntry {
	id: "1" | "2" | "3" | "4";
	Icon: LucideIcon;
}

const BELIEFS: ReadonlyArray<BeliefEntry> = [
	{ id: "1", Icon: Heart },
	{ id: "2", Icon: Sparkles },
	{ id: "3", Icon: Compass },
	{ id: "4", Icon: Target },
];

export function AboutPage() {
	const { t } = useTranslation("about");

	return (
		<>
			<PageHelmet title={t("meta.title")} description={t("meta.description")} />

			<section className="bg-mesh relative isolate overflow-hidden pt-20 pb-16 md:pt-32 md:pb-20">
				<div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
					<SectionHeading
						as="h1"
						eyebrow={<EyebrowBadge tone="ink">{t("hero.eyebrow")}</EyebrowBadge>}
						title={t("hero.title")}
						titleAccent={t("hero.title_accent")}
					/>
				</div>
			</section>

			<section className="py-20 md:py-24">
				<div className="mx-auto grid max-w-6xl gap-6 px-4 md:grid-cols-2 sm:px-6 lg:px-8">
					<Reveal>
						<article className="h-full rounded-3xl border border-default-200 bg-white p-8 shadow-card">
							<p className="text-xs font-bold uppercase tracking-[0.16em] text-brand-700">{t("vision.eyebrow")}</p>
							<h2 className="mt-3 text-balance text-2xl font-bold tracking-tight text-foreground md:text-3xl">{t("vision.title")}</h2>
							<p className="mt-4 text-base leading-relaxed text-default-600">{t("vision.body")}</p>
						</article>
					</Reveal>
					<Reveal delay={0.1}>
						<article className="bg-brand-gradient h-full rounded-3xl p-8 text-white shadow-lift">
							<p className="text-xs font-bold uppercase tracking-[0.16em] text-white/85">{t("mission.eyebrow")}</p>
							<h2 className="mt-3 text-balance text-2xl font-bold tracking-tight md:text-3xl">{t("mission.title")}</h2>
							<p className="mt-4 text-base leading-relaxed text-white/90">{t("mission.body")}</p>
						</article>
					</Reveal>
				</div>
			</section>

			<AnimatedSection className="py-20 md:py-24">
				<div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
					<div className="space-y-6 text-base leading-relaxed text-default-700 md:text-lg">
						<p>{t("story.paragraph_1")}</p>
						<p>{t("story.paragraph_2")}</p>
						<p>{t("story.paragraph_3")}</p>
					</div>
				</div>
			</AnimatedSection>

			<section className="border-t border-default-200/60 bg-content1/40 py-24 md:py-32">
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
					<SectionHeading title={t("beliefs.title")} titleAccent={t("beliefs.title_accent")} description={t("beliefs.subtitle")} />
					<div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
						{BELIEFS.map(({ id, Icon }, index) => (
							<Reveal key={id} delay={index * 0.08}>
								<article className="flex h-full flex-col gap-4 rounded-3xl border border-default-200 bg-white p-7 shadow-card hover-lift">
									<span className="inline-flex size-11 items-center justify-center rounded-2xl bg-brand-50 text-brand-700">
										<Icon className="h-5 w-5" />
									</span>
									<h3 className="text-lg font-bold text-foreground">{t(`beliefs.items.${id}.title`)}</h3>
									<p className="text-sm leading-relaxed text-default-600">{t(`beliefs.items.${id}.body`)}</p>
								</article>
							</Reveal>
						))}
					</div>
				</div>
			</section>
		</>
	);
}
